import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import css from './PaymentDetails.css';

const CARD_LOGOS = {
  visa: 'https://img.icons8.com/color/48/000000/visa.png',
  mastercard: 'https://img.icons8.com/color/48/000000/mastercard.png',
  amex: 'https://img.icons8.com/color/48/000000/amex.png',
  default: 'https://img.icons8.com/ios-filled/48/000000/credit-card-front.png'
};

export const getCardStatus = (expDateString) => {
  if (!expDateString) return 'valid';
  const expDate = new Date(expDateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), 1);
  const cardDate = new Date(expDate.getFullYear(), expDate.getMonth(), 1);

  if (cardDate < today) return 'expired';
  const diffMonths = (expDate.getFullYear() - now.getFullYear()) * 12 + (expDate.getMonth() - now.getMonth());
  return diffMonths <= 1 ? 'warning' : 'valid';
};

const PaymentDetails = (props) => {
  const {
    subscription,
    cardInfo,
    nextPaymentDate,
    onEdit,
    intl,
    isStarting,
    isProvider,
    isExpired: isExpiredProp,
  } = props;

  const processState = subscription?.attributes?.processState;
  const unpaidSince = subscription?.attributes?.unpaidSince;
  const nextPaymentAttemptAt = subscription?.attributes?.nextPaymentAttemptAt;
  const monthlyPrice = subscription?.attributes?.monthlyPrice;
  const subscriptionId = subscription?.id?.uuid;

  const isExpired = isExpiredProp || processState === 'payment-expired' || processState === 'declined';
  const isPending = processState === 'payment-pending';
  const isInactive = processState === 'completed' || processState === 'cancelled';
  const isWaitingForPickup = processState === 'accepted';
  const hasUnpaidIssue = processState === 'active' && !!unpaidSince;

  if (isInactive) {
    return (
      <div className={css.inactivePaymentBox}>
        <p className={css.sectionText}>
          {processState === 'completed' ? (
            <FormattedMessage id="PaymentDetails.subscriptionCompleted" defaultValue="Subskrypcja została zakończona." />
          ) : (
            <FormattedMessage id="PaymentDetails.subscriptionCancelled" defaultValue="Subskrypcja została anulowana." />
          )}
        </p>
        <div className={css.inactiveStatusBadge}>
          <FormattedMessage id="PaymentDetails.noFurtherCharges" defaultValue="Brak dalszych opłat" />
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className={css.inactivePaymentBox}>
        <p className={css.sectionText}>
          <FormattedMessage
            id={isProvider ? "PaymentDetails.providerPaymentExpired" : "PaymentDetails.customerPaymentExpired"}
            defaultValue={
              isProvider
                ? "Rezerwacja wygasła z powodu braku płatności ze strony klienta."
                : "Czas na płatność minął. Ta rezerwacja wygasła."
            }
          />
        </p>
        <div className={css.inactiveStatusBadge}>
          <FormattedMessage id="PaymentDetails.noFurtherCharges" defaultValue="Brak dalszych opłat" />
        </div>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className={css.pendingPaymentBox}>
        <div className={css.sectionText}>
          <p className={css.pendingText}>
            <FormattedMessage
              id={isProvider ? "PaymentDetails.providerPendingText" : "PaymentDetails.customerPendingText"}
              defaultValue={
                isProvider
                  ? "Oczekujemy na dokończenie płatności inicjalnej przez klienta."
                  : "Oczekujemy na dokończenie opłaty inicjalnej za tę subskrypcję."
              }
            />
          </p>
        </div>
        {!isProvider && subscriptionId && (
          <Link to={`/subpayment/${subscriptionId}`} className={css.payNowBtn}>
            <FormattedMessage id="PaymentDetails.btnPayNow" defaultValue="Opłać teraz" />
          </Link>
        )}
      </div>
    );
  }

  if (isProvider) {
    return (
      <div className={css.paymentDetailsContainer}>
        <div className={css.sectionText}>
          {hasUnpaidIssue ? (
            <div className={css.unpaidNotice}>
              <p className={css.unpaidNoticeText}>
                <FormattedMessage
                  id="PaymentDetails.providerUnpaidNotice"
                  defaultValue="Klient posiada zaległość w opłacie od: {date}."
                  values={{ date: <strong>{intl.formatDate(unpaidSince)}</strong> }}
                />
              </p>
            </div>
          ) : isWaitingForPickup ? (
            <FormattedMessage
              id="PaymentDetails.providerWaitingForPickup"
              defaultValue="Pierwsza opłata została pobrana. Kolejne automatyczne opłaty ruszą po wydaniu sprzętu."
            />
          ) : (
            <FormattedMessage
              id="PaymentDetails.providerNextRenewal"
              defaultValue="Kolejna automatyczna opłata {amount} zostanie pobrana od klienta: {date}"
              values={{
                date: <strong>{intl.formatDate(nextPaymentDate)}</strong>,
                amount: <strong>{formatMoney(intl, monthlyPrice)}</strong>
              }}
            />
          )}
        </div>

        <div className={css.providerCardBox}>
          <span className={classNames(css.providerCardBadge, { [css.providerCardError]: hasUnpaidIssue })}>
            {hasUnpaidIssue ? (
              <FormattedMessage id="PaymentDetails.providerCardError" defaultValue="⚠️ Karta klienta: Problem z obciążeniem" />
            ) : (
              <FormattedMessage id="PaymentDetails.providerCardValid" defaultValue="✓ Karta płatnicza klienta jest podpięta" />
            )}
          </span>
        </div>
      </div>
    );
  }

  const cardStatus = cardInfo ? getCardStatus(cardInfo.exp) : 'valid';
  const [year, month] = cardInfo?.exp ? cardInfo.exp.split('-') : ['', ''];
  const formattedExp = cardInfo?.exp ? `${month}/${year.slice(-2)}` : '';

  return (
    <div className={css.paymentDetailsContainer}>
      <div className={css.sectionText}>
        {hasUnpaidIssue ? (
          <div className={css.unpaidNotice}>
            <p className={css.unpaidNoticeText}>
              <FormattedMessage
                id="PaymentDetails.customerUnpaidNotice"
                defaultValue="Nie udało się pobrać opłaty z dnia: {date}."
                values={{ date: <strong>{intl.formatDate(unpaidSince)}</strong> }}
              />
            </p>
            {nextPaymentAttemptAt && (
              <p className={css.nextAttemptText}>
                <FormattedMessage
                  id="PaymentDetails.nextAttemptText"
                  defaultValue="Kolejna próba pobrania kwoty {amount}: {date}"
                  values={{
                    amount: <strong>{formatMoney(intl, monthlyPrice)}</strong>,
                    date: <strong>{intl.formatDate(nextPaymentAttemptAt)}</strong>
                  }}
                />
              </p>
            )}
          </div>
        ) : isWaitingForPickup ? (
          <FormattedMessage
            id="PaymentDetails.waitingForPickupCharge"
            defaultValue="Opłata początkowa została pobrana. Kolejne płatności będą pobierane co miesiąc po odbiorze."
          />
        ) : (
          <FormattedMessage
            id="PaymentDetails.nextRenewal"
            defaultValue="Kolejna opłata {amount} zostanie pobrana: {date}"
            values={{
              date: <strong>{intl.formatDate(nextPaymentDate)}</strong>,
              amount: <strong>{formatMoney(intl, monthlyPrice)}</strong>
            }}
          />
        )}
      </div>

      <div className={classNames(css.cardBox, { [css.cardBoxError]: hasUnpaidIssue })}>
        <div className={css.cardInfo}>
          <div className={css.cardLogoWrapper}>
            <img
              src={CARD_LOGOS[cardInfo?.name?.toLowerCase()] || CARD_LOGOS.default}
              alt="Card logo"
              className={css.cardLogo}
            />
          </div>

          {cardInfo ? (
            <div className={css.cardDetails}>
              <div className={css.cardMainRow}>
                <div className={css.cardNumber}>
                  <span className={css.last4}>•••• {cardInfo.last4}</span>
                </div>
                <span className={css.cardExpiry}>{formattedExp}</span>
              </div>
              {cardStatus === 'warning' && (
                <span className={css.statusAlert}>
                  <FormattedMessage id="PaymentDetails.statusWarning" defaultValue="Karta wkrótce wygasa" />
                </span>
              )}
              {(cardStatus === 'expired' || hasUnpaidIssue) && (
                <span className={css.statusError}>
                  <FormattedMessage
                    id={hasUnpaidIssue ? "PaymentDetails.statusUnpaid" : "PaymentDetails.statusExpired"}
                    defaultValue={hasUnpaidIssue ? "Wymagana zmiana karty" : "Karta wygasła"}
                  />
                </span>
              )}
            </div>
          ) : (
            <span className={css.noCard}>
              <FormattedMessage id="PaymentDetails.noCard" defaultValue="Brak podpiętej karty" />
            </span>
          )}
        </div>

        <button className={css.changeCardBtn} onClick={onEdit} disabled={isStarting}>
          {isStarting ? '...' : (
            <FormattedMessage
              id={hasUnpaidIssue ? "PaymentDetails.updateCardBtn" : "PaymentDetails.editBtn"}
              defaultValue={hasUnpaidIssue ? "Zaktualizuj" : "Zmień"}
            />
          )}
        </button>
      </div>
    </div>
  );
};

export default injectIntl(PaymentDetails);